/**
 * GridMapSidebar — Tabbed sidebar for the grid map game view.
 * Contains character mini-strip (always visible) + tabbed content panels:
 * Chat, Character, World, and Controls.
 * All data is mock/hardcoded for now; structured for future store integration.
 */

import { useState, useRef, useEffect } from 'react';
import {
  MessageSquare, User, Globe, Settings,
  Heart, Zap, Shield, Swords, Eye, Wind, Sparkles,
  Send, MapPin, Clock, Skull,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { CharacterMiniStrip } from './CharacterMiniStrip';
import type { RenderConfig } from '@omen-quest/grid-map';
import type { DungeonContent } from '@/lib/grid-map-ai';

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_CHARACTER = {
  name: 'Kael Stormborn',
  imageUrl: null,
  vitals: { current: 7, max: 10 },
  resolve: { current: 4, max: 6 },
  stats: { brawn: 3, grace: 2, wits: 4, spirit: 1 } as Record<string, number>,
  tagsPositive: ['Quick Reflexes', 'Silver Tongue'],
  tagsNegative: ['Reckless'],
  inventory: [
    { name: 'Iron Shortsword', rarity: 'common' },
    { name: 'Healing Potion', rarity: 'uncommon' },
    { name: 'Thieves\' Tools', rarity: 'common' },
  ] as { name: string; rarity: string }[],
};

const MOCK_MESSAGES = [
  { role: 'assistant' as const, content: 'You step through the ancient doorway into the Sunken Archive. The air is thick with dust and the faint scent of old parchment. Pale light filters through cracked stained glass above.' },
  { role: 'user' as const, content: 'I look around the room carefully.' },
  { role: 'assistant' as const, content: 'The chamber is dimly lit by bioluminescent fungi clinging to the vaulted ceiling. Rows of stone shelves line the walls, most collapsed or half-buried in rubble. A narrow passage leads deeper to the north.' },
  { role: 'user' as const, content: 'I search the intact shelves for anything useful.' },
  { role: 'assistant' as const, content: 'Among the debris you find a crumbling scroll case. Inside, a partially legible map shows passages beneath the city — including one marked with a warning sigil you don\'t recognize.' },
];

const MOCK_WORLD = {
  locationName: 'The Sunken Archive',
  phase: 'venture' as const,
  timeOfDay: 'Dusk',
  shadowPool: 3,
};

// ============================================================================
// TYPES
// ============================================================================

type TabKey = 'chat' | 'character' | 'world' | 'controls';

interface Tab {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
}

const TABS: Tab[] = [
  { key: 'chat', label: 'Chat', icon: <MessageSquare className="w-3.5 h-3.5" /> },
  { key: 'character', label: 'Char', icon: <User className="w-3.5 h-3.5" /> },
  { key: 'world', label: 'World', icon: <Globe className="w-3.5 h-3.5" /> },
  { key: 'controls', label: 'Ctrl', icon: <Settings className="w-3.5 h-3.5" /> },
];

// ============================================================================
// STAT ICONS
// ============================================================================

const STAT_ICONS: Record<string, typeof Heart> = {
  brawn: Swords,
  grace: Wind,
  wits: Eye,
  spirit: Sparkles,
};

const RARITY_COLORS: Record<string, string> = {
  common: 'text-neutral-400',
  uncommon: 'text-emerald-400',
  rare: 'text-blue-400',
  epic: 'text-purple-400',
  legendary: 'text-amber-400',
};

// ============================================================================
// SIDEBAR COMPONENT
// ============================================================================

interface GridMapSidebarProps {
  renderConfig: RenderConfig;
  onRenderConfigChange: (config: RenderConfig) => void;
  dungeonContent?: DungeonContent | null;
  currentRoom?: { name: string; description: string } | null;
  visitedRoomIds?: Set<number>;
}

export function GridMapSidebar({
  renderConfig, onRenderConfigChange,
  dungeonContent, currentRoom, visitedRoomIds,
}: GridMapSidebarProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('chat');

  return (
    <div className="flex flex-col h-full bg-neutral-950 border-l border-neutral-800">
      {/* Character mini-strip — always visible */}
      <CharacterMiniStrip />

      {/* Tab bar */}
      <div className="flex border-b border-neutral-800">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors',
              activeTab === tab.key
                ? 'text-amber-400 border-b-2 border-amber-400 bg-amber-500/5'
                : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50',
            )}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === 'chat' && <ChatPanel />}
        {activeTab === 'character' && <CharacterPanel />}
        {activeTab === 'world' && (
          <WorldPanel
            dungeonContent={dungeonContent}
            currentRoom={currentRoom}
            visitedRoomIds={visitedRoomIds}
          />
        )}
        {activeTab === 'controls' && (
          <ControlsPanel
            config={renderConfig}
            onChange={onRenderConfigChange}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CHAT PANEL
// ============================================================================

function ChatPanel() {
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {MOCK_MESSAGES.map((msg, i) => (
          <div
            key={i}
            className={cn(
              'max-w-[90%] rounded-lg px-3 py-2 text-sm leading-relaxed',
              msg.role === 'assistant'
                ? 'bg-neutral-900 text-neutral-200 border border-neutral-800'
                : 'ml-auto bg-neutral-800 text-white',
            )}
          >
            {msg.content}
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="border-t border-neutral-800 p-3">
        <form
          onSubmit={e => {
            e.preventDefault();
            if (input.trim()) setInput('');
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="What do you do?"
            className="flex-1 bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-neutral-700 transition-colors"
          />
          <button
            type="submit"
            className={cn(
              'px-3 py-2 rounded-lg transition-colors flex items-center justify-center',
              input.trim()
                ? 'bg-white text-black hover:bg-neutral-200'
                : 'bg-neutral-900 text-neutral-600 cursor-not-allowed',
            )}
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// CHARACTER PANEL
// ============================================================================

function CharacterPanel() {
  const char = MOCK_CHARACTER;

  return (
    <div className="p-3 space-y-4">
      {/* Stats */}
      <Section title="Attributes">
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(char.stats).map(([stat, value]) => {
            const Icon = STAT_ICONS[stat] ?? Shield;
            return (
              <div key={stat} className="flex items-center gap-2 bg-neutral-900 rounded-lg px-3 py-2">
                <Icon className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-neutral-400 uppercase tracking-wide flex-1">{stat}</span>
                <span className="text-sm font-mono text-white font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Vitals & Resolve bars */}
      <Section title="Resources">
        <div className="space-y-2.5">
          <StatBar label="Vitals" current={char.vitals.current} max={char.vitals.max} color="red" icon={Heart} />
          <StatBar label="Resolve" current={char.resolve.current} max={char.resolve.max} color="blue" icon={Zap} />
        </div>
      </Section>

      {/* Tags */}
      <Section title="Traits">
        <div className="flex flex-wrap gap-1.5">
          {char.tagsPositive.map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              {tag}
            </span>
          ))}
          {char.tagsNegative.map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
              {tag}
            </span>
          ))}
        </div>
      </Section>

      {/* Inventory */}
      <Section title="Inventory">
        <div className="space-y-1.5">
          {char.inventory.map(item => (
            <div key={item.name} className="flex items-center justify-between bg-neutral-900 rounded px-3 py-1.5">
              <span className="text-sm text-neutral-200">{item.name}</span>
              <span className={cn('text-xs capitalize', RARITY_COLORS[item.rarity] ?? 'text-neutral-500')}>
                {item.rarity}
              </span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ============================================================================
// WORLD PANEL
// ============================================================================

interface WorldPanelProps {
  dungeonContent?: DungeonContent | null;
  currentRoom?: { name: string; description: string } | null;
  visitedRoomIds?: Set<number>;
}

function WorldPanel({ dungeonContent, currentRoom, visitedRoomIds }: WorldPanelProps) {
  const world = MOCK_WORLD;
  const shadowLevel = Math.min(world.shadowPool, 10);
  const dangerLevel = shadowLevel <= 3 ? 'low' : shadowLevel <= 6 ? 'moderate' : 'high';

  const totalRooms = dungeonContent?.rooms.length ?? 0;
  const visitedCount = visitedRoomIds?.size ?? 0;

  return (
    <div className="p-3 space-y-4">
      {/* Dungeon story context (shown when AI populated) */}
      {dungeonContent && (
        <>
          <Section title="Quest">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-amber-400">{dungeonContent.story.title}</h3>
              <p className="text-xs text-neutral-300 leading-relaxed">{dungeonContent.story.hook}</p>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-neutral-500 uppercase tracking-wide">Objective:</span>
                <span className="text-neutral-200">{dungeonContent.story.objective}</span>
              </div>
            </div>
          </Section>

          {/* Current room */}
          {currentRoom && (
            <Section title="Current Room">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-amber-400" />
                  <span className="text-neutral-200">{currentRoom.name}</span>
                </div>
                <p className="text-xs text-neutral-400 leading-relaxed">{currentRoom.description}</p>
              </div>
            </Section>
          )}

          {/* Progression */}
          <Section title="Exploration">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-neutral-400">Rooms discovered</span>
                <span className="font-mono text-neutral-300">
                  {visitedCount}<span className="text-neutral-600">/{totalRooms}</span>
                </span>
              </div>
              <div className="h-1.5 bg-neutral-900 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all duration-500"
                  style={{ width: totalRooms > 0 ? `${(visitedCount / totalRooms) * 100}%` : '0%' }}
                />
              </div>
              {dungeonContent.progression.sequence && (
                <p className="text-xs text-neutral-500 italic leading-relaxed">
                  {dungeonContent.progression.sequence}
                </p>
              )}
            </div>
          </Section>
        </>
      )}

      {/* Location (fallback when no dungeon content) */}
      {!dungeonContent && (
        <Section title="Location">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-amber-400" />
            <span className="text-neutral-200">{world.locationName}</span>
          </div>
        </Section>
      )}

      {/* Phase */}
      <Section title="Phase">
        <div className="flex gap-1">
          {(['venture', 'fray', 'hearth'] as const).map(phase => (
            <div
              key={phase}
              className={cn(
                'flex-1 py-1.5 text-xs capitalize rounded text-center transition-colors',
                world.phase === phase
                  ? 'bg-white text-black font-medium'
                  : 'bg-neutral-900 text-neutral-500',
              )}
            >
              {phase}
            </div>
          ))}
        </div>
      </Section>

      {/* Time */}
      <Section title="Time">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-200">{world.timeOfDay}</span>
        </div>
      </Section>

      {/* Shadow Pool */}
      <Section title="Shadow Pool">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Skull className="w-4 h-4 text-neutral-400" />
              <span className="text-xs text-neutral-400 uppercase tracking-wide">Darkness</span>
            </div>
            <span
              className={cn(
                'text-xs font-mono',
                dangerLevel === 'high' ? 'text-red-500'
                  : dangerLevel === 'moderate' ? 'text-amber-500'
                  : 'text-neutral-500',
              )}
            >
              {world.shadowPool}
            </span>
          </div>
          <div className="flex gap-0.5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'h-2 flex-1 rounded-sm transition-all duration-300',
                  i < shadowLevel
                    ? dangerLevel === 'high' ? 'bg-red-500'
                      : dangerLevel === 'moderate' ? 'bg-amber-500'
                      : 'bg-neutral-600'
                    : 'bg-neutral-900',
                )}
              />
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}

// ============================================================================
// CONTROLS PANEL
// ============================================================================

function ControlsPanel({ config, onChange }: { config: RenderConfig; onChange: (c: RenderConfig) => void }) {
  const toggles: { key: keyof RenderConfig; label: string }[] = [
    { key: 'fogEnabled', label: 'Fog of War' },
    { key: 'showGrid', label: 'Grid Lines' },
    { key: 'showMinimap', label: 'Minimap' },
    { key: 'showTriggers', label: 'Show Triggers' },
    { key: 'showDebugVis', label: 'Debug Visibility' },
  ];

  return (
    <div className="p-3 space-y-4">
      <Section title="Render Toggles">
        <div className="space-y-2">
          {toggles.map(({ key, label }) => (
            <label key={key} className="flex items-center justify-between cursor-pointer group">
              <span className="text-sm text-neutral-300 group-hover:text-white transition-colors">{label}</span>
              <button
                onClick={() => onChange({ ...config, [key]: !config[key] })}
                className={cn(
                  'w-9 h-5 rounded-full transition-colors relative',
                  config[key] ? 'bg-amber-500' : 'bg-neutral-700',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                    config[key] ? 'translate-x-4' : 'translate-x-0.5',
                  )}
                />
              </button>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Tile Size">
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={16}
            max={48}
            value={config.tileSize}
            onChange={e => onChange({ ...config, tileSize: Number(e.target.value) })}
            className="flex-1 accent-amber-500"
          />
          <span className="text-xs font-mono text-neutral-400 w-8 text-right">{config.tileSize}px</span>
        </div>
      </Section>

      <Section title="Keyboard Shortcuts">
        <div className="space-y-1.5 text-neutral-500 text-xs">
          <p><kbd className="text-neutral-300">WASD</kbd> / <kbd className="text-neutral-300">Arrows</kbd> — Move</p>
          <p><kbd className="text-neutral-300">Scroll</kbd> — Zoom</p>
          <p><kbd className="text-neutral-300">Drag</kbd> — Pan</p>
          <p><kbd className="text-neutral-300">F</kbd> — Toggle fog</p>
          <p><kbd className="text-neutral-300">G</kbd> — Toggle grid</p>
          <p><kbd className="text-neutral-300">T</kbd> — Toggle triggers</p>
        </div>
      </Section>
    </div>
  );
}

// ============================================================================
// SHARED SUB-COMPONENTS
// ============================================================================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">{title}</h4>
      {children}
    </div>
  );
}

function StatBar({ label, current, max, color, icon: Icon }: {
  label: string;
  current: number;
  max: number;
  color: 'red' | 'blue';
  icon: typeof Heart;
}) {
  const pct = (current / max) * 100;
  const colorClasses = color === 'red'
    ? { icon: 'text-red-500', bar: 'bg-red-500' }
    : { icon: 'text-blue-500', bar: 'bg-blue-500' };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5">
          <Icon className={cn('h-3.5 w-3.5', colorClasses.icon)} />
          <span className="text-neutral-400 uppercase tracking-wide">{label}</span>
        </div>
        <span className="font-mono text-neutral-300">
          {current}<span className="text-neutral-600">/{max}</span>
        </span>
      </div>
      <div className="h-1.5 bg-neutral-900 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', colorClasses.bar)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
